// src/pages/IndoorNavigationPreview/index.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Link,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  Chip,
  Divider,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
  Fab,
  SwipeableDrawer,
} from '@mui/material';
import {
  Map as MapIcon,
  NavigateNext as NavigateNextIcon,
  ThreeDRotation as ThreeDIcon,
  Navigation as NavigationIcon,
  PlayArrow as PlayArrowIcon,
  DirectionsWalk as StepByStepIcon,
  Layers as LayersIcon,
  Stairs as StairsIcon,
  Elevator as ElevatorIcon,
  DoorFront as EntranceIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

// Components
import Building3DViewer from '../../components/maps/preview/Building3DViewer';
import NavigationSimulator from '../../components/maps/preview/NavigationSimulator';

// Services
import indoorMappingService from '../../services/indoorMappingService';
import { locationsApi } from '../../services/apiClient';

const IndoorNavigationPreview = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // View mode: '3d' | 'overview' | 'stepbystep'
  // '3d' = exploring building, 'overview' = route calculated showing 3D overview, 'stepbystep' = active navigation
  const [viewMode, setViewMode] = useState('3d');
  // Mobile UI state
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Location and floor plan state
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [floorPlans, setFloorPlans] = useState([]);
  const [buildingData, setBuildingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Navigation state
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [entrances, setEntrances] = useState([]);
  const [selectedEntrance, setSelectedEntrance] = useState(null);
  const [route, setRoute] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Auto-fullscreen on mobile when viewing 3D
  useEffect(() => {
    if (isMobile && buildingData) {
      setIsFullscreen(true);
    }
  }, [isMobile, buildingData]);

  // Load locations on mount
  useEffect(() => {
    loadLocations();
  }, []);

  // Load building data when location changes
  useEffect(() => {
    if (selectedLocationId) {
      loadBuildingData(selectedLocationId);
    } else {
      setFloorPlans([]);
      setBuildingData(null);
    }
  }, [selectedLocationId]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const response = await locationsApi.getAll();
      if (response.data.success) {
        const allLocations = response.data.data || [];
        // Filter to buildings (location type 1)
        const buildings = allLocations.filter(loc =>
          loc.locationType === 1 || loc.location_type === 1
        );
        setLocations(buildings);
      }
    } catch (err) {
      console.error('Failed to load locations:', err);
      setError('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const loadBuildingData = async (locationId) => {
    try {
      setLoading(true);
      setError(null);

      // Load floor plans
      const floorPlansData = await indoorMappingService.getFloorPlans(locationId);
      setFloorPlans(floorPlansData || []);

      // Load entrances
      const entrancesData = await indoorMappingService.getEntrances(locationId);
      setEntrances(entrancesData || []);
      if (entrancesData?.length > 0) {
        setSelectedEntrance(entrancesData[0]);
      }

      // Build comprehensive building data for 3D view
      const buildingDataPromises = (floorPlansData || []).map(async (fp) => {
        const graph = await indoorMappingService.getWayfindingGraph(fp.id);
        return {
          ...fp,
          nodes: graph.nodes || [],
          edges: graph.edges || [],
        };
      });

      const floorsWithData = await Promise.all(buildingDataPromises);

      setBuildingData({
        locationId,
        floors: floorsWithData.sort((a, b) => a.floor_number - b.floor_number),
        entrances: entrancesData || [],
      });

    } catch (err) {
      console.error('Failed to load building data:', err);
      setError(err.message || 'Failed to load building data');
      setFloorPlans([]);
      setBuildingData(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle destination selection from 3D view
  const handleDestinationSelect = async (node, floorPlan) => {
    setSelectedDestination({ node, floorPlan });
    setCurrentStepIndex(0); // Reset step index

    // If we have an entrance, calculate route
    if (selectedEntrance) {
      try {
        const routeData = await indoorMappingService.calculateRouteToNode(
          selectedEntrance.id,
          node.id
        );
        console.log('[IndoorNavigationPreview] Route data from API:', routeData);
        console.log('[IndoorNavigationPreview] Route steps:', routeData?.steps);
        setRoute(routeData);
        // Switch to route overview mode (3D view with route displayed)
        setViewMode('overview');
      } catch (err) {
        console.error('Failed to calculate route:', err);
        setError('Failed to calculate route to destination');
      }
    }
  };

  // Start step-by-step navigation
  const handleStartNavigation = () => {
    setCurrentStepIndex(0);
    setViewMode('stepbystep');
  };

  // Go back to route overview
  const handleBackToOverview = () => {
    setViewMode('overview');
  };

  // Cancel navigation and go back to 3D exploration
  const handleCancelNavigation = () => {
    setRoute(null);
    setSelectedDestination(null);
    setViewMode('3d');
  };

  // Get building stats
  const getBuildingStats = () => {
    if (!buildingData) return null;

    let totalNodes = 0;
    let totalEdges = 0;
    let stairsNodes = 0;
    let elevatorNodes = 0;

    buildingData.floors.forEach(floor => {
      totalNodes += floor.nodes?.length || 0;
      totalEdges += floor.edges?.length || 0;
      floor.nodes?.forEach(node => {
        if (node.node_type === 'stairs') stairsNodes++;
        if (node.node_type === 'elevator') elevatorNodes++;
      });
    });

    return {
      floors: buildingData.floors.length,
      nodes: totalNodes,
      edges: totalEdges,
      stairs: stairsNodes,
      elevators: elevatorNodes,
      entrances: entrances.length,
    };
  };

  const stats = getBuildingStats();

  // Transform route steps into format expected by Building3DViewer
  // API format: { start_node_id, end_node_id, floor_plan_id, path_geometry, floor_change_type }
  // 3D Viewer format: { node_id, floor_plan_id, x_position, y_position, node_type }
  const getNavigationRouteFor3D = () => {
    console.log('[getNavigationRouteFor3D] route:', route);
    console.log('[getNavigationRouteFor3D] route?.steps:', route?.steps);
    console.log('[getNavigationRouteFor3D] buildingData?.floors:', buildingData?.floors?.length);

    if (!route?.steps || !buildingData?.floors) {
      console.log('[getNavigationRouteFor3D] Returning null - missing data');
      return null;
    }

    // Build node map for looking up node types
    const nodeMap = new Map();
    buildingData.floors.forEach(floor => {
      floor.nodes?.forEach(node => {
        nodeMap.set(node.id, node);
      });
    });

    const routeSteps = [];
    const seenNodes = new Set();

    route.steps.forEach((step) => {
      // Add start_node using path_geometry coordinates
      if (step.start_node_id && !seenNodes.has(step.start_node_id)) {
        const node = nodeMap.get(step.start_node_id);
        // Determine node_type: use floor_change_type if this step has a floor change
        let nodeType = node?.node_type || 'normal';

        routeSteps.push({
          node_id: step.start_node_id,
          floor_plan_id: step.floor_plan_id,
          x_position: step.path_geometry?.start?.x ?? node?.x_position ?? 0,
          y_position: step.path_geometry?.start?.y ?? node?.y_position ?? 0,
          node_type: nodeType,
        });
        seenNodes.add(step.start_node_id);
      }

      // Add end_node using path_geometry coordinates
      if (step.end_node_id && !seenNodes.has(step.end_node_id)) {
        const node = nodeMap.get(step.end_node_id);
        // If this step has a floor change, mark the end node with the change type
        let nodeType = node?.node_type || 'normal';
        if (step.floor_change_type) {
          nodeType = step.floor_change_type; // 'stairs' or 'elevator'
        }

        routeSteps.push({
          node_id: step.end_node_id,
          floor_plan_id: step.floor_change_to || step.floor_plan_id,
          x_position: step.path_geometry?.end?.x ?? node?.x_position ?? 0,
          y_position: step.path_geometry?.end?.y ?? node?.y_position ?? 0,
          node_type: nodeType,
        });
        seenNodes.add(step.end_node_id);
      }
    });

    console.log('[IndoorNavigationPreview] Route steps for 3D:', routeSteps);
    return routeSteps.length > 0 ? routeSteps : null;
  };

  const navigationRouteFor3D = getNavigationRouteFor3D();

  // Debug: Log navigation mode conditions
  console.log('[IndoorNavigationPreview] viewMode:', viewMode, 'route:', route, 'navigationRouteFor3D:', navigationRouteFor3D);
  console.log('[IndoorNavigationPreview] Should show 3D nav?', viewMode === 'navigate' && navigationRouteFor3D);

  // Mobile controls drawer content
  const MobileControlsContent = () => (
    <Box sx={{ p: 2, width: 300 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Settings</Typography>
        <IconButton onClick={() => setMobileDrawerOpen(false)}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Building Selection */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Building</InputLabel>
        <Select
          value={selectedLocationId}
          label="Building"
          onChange={(e) => {
            setSelectedLocationId(e.target.value);
            setViewMode('3d');
            setRoute(null);
            setSelectedDestination(null);
          }}
        >
          <MenuItem value="">
            <em>Choose a building...</em>
          </MenuItem>
          {locations
            .sort((a, b) => (a.friendlyName || '').localeCompare(b.friendlyName || ''))
            .map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>
                {loc.friendlyName || loc.name || `Location ${loc.id}`}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {/* Entrance Selection */}
      {entrances.length > 0 && (
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Start From</InputLabel>
          <Select
            value={selectedEntrance?.id || ''}
            label="Start From"
            onChange={(e) => {
              const entrance = entrances.find(en => en.id === e.target.value);
              setSelectedEntrance(entrance);
            }}
          >
            {entrances.map((entrance) => (
              <MenuItem key={entrance.id} value={entrance.id}>
                {entrance.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Navigation Actions */}
      {viewMode === 'overview' && route && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Route Ready</Typography>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<PlayArrowIcon />}
            onClick={() => {
              handleStartNavigation();
              setMobileDrawerOpen(false);
            }}
            sx={{ mb: 1 }}
          >
            Start Navigation
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              handleCancelNavigation();
              setMobileDrawerOpen(false);
            }}
          >
            Cancel Route
          </Button>
        </>
      )}

      {viewMode === 'stepbystep' && (
        <>
          <Divider sx={{ my: 2 }} />
          <Button
            variant="outlined"
            fullWidth
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              handleBackToOverview();
              setMobileDrawerOpen(false);
            }}
            sx={{ mb: 1 }}
          >
            Back to Overview
          </Button>
          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={() => {
              handleCancelNavigation();
              setMobileDrawerOpen(false);
            }}
          >
            End Navigation
          </Button>
        </>
      )}

      {/* Stats */}
      {stats && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Building Info</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip icon={<LayersIcon />} label={`${stats.floors} Floors`} size="small" />
            <Chip icon={<StairsIcon />} label={`${stats.stairs} Stairs`} size="small" />
            <Chip icon={<ElevatorIcon />} label={`${stats.elevators} Elevators`} size="small" />
            <Chip icon={<EntranceIcon />} label={`${stats.entrances} Entrances`} size="small" />
          </Box>
        </>
      )}
    </Box>
  );

  // Fullscreen 3D/Navigation view for mobile
  if (isMobile && isFullscreen && buildingData) {
    return (
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1300,
        bgcolor: 'background.default',
      }}>
        {/* Floating controls */}
        <Box sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          right: 8,
          zIndex: 10,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pointerEvents: 'none',
        }}>
          {/* Left controls */}
          <Box sx={{ display: 'flex', gap: 1, pointerEvents: 'auto' }}>
            <Fab
              size="small"
              onClick={() => setIsFullscreen(false)}
              sx={{ bgcolor: 'background.paper' }}
            >
              <FullscreenExitIcon />
            </Fab>
            <Fab
              size="small"
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ bgcolor: 'background.paper' }}
            >
              <SettingsIcon />
            </Fab>
          </Box>

          {/* Right controls - Status indicator */}
          <Box sx={{ pointerEvents: 'auto' }}>
            {viewMode === 'stepbystep' && (
              <Chip
                icon={<NavigationIcon />}
                label="Navigating"
                color="success"
                sx={{ bgcolor: 'success.main', color: 'white' }}
              />
            )}
            {viewMode === 'overview' && (
              <Chip
                icon={<ThreeDIcon />}
                label="Route Preview"
                color="primary"
              />
            )}
          </Box>
        </Box>

        {/* Building name chip */}
        {selectedLocationId && (
          <Chip
            label={locations.find(l => l.id === selectedLocationId)?.friendlyName || 'Building'}
            size="small"
            color="default"
            sx={{
              position: 'absolute',
              top: 60,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10,
              bgcolor: 'background.paper',
            }}
          />
        )}

        {/* Main content */}
        <Box sx={{ height: '100%', width: '100%' }}>
          {viewMode === '3d' ? (
            <Building3DViewer
              buildingData={buildingData}
              onNodeSelect={handleDestinationSelect}
              selectedDestination={selectedDestination}
              isMobile={true}
              isNavigating={false}
            />
          ) : viewMode === 'overview' && navigationRouteFor3D ? (
            <Building3DViewer
              buildingData={buildingData}
              onNodeSelect={handleDestinationSelect}
              selectedDestination={selectedDestination}
              isMobile={true}
              isNavigating={true}
              navigationRoute={navigationRouteFor3D}
              currentStepIndex={currentStepIndex}
            />
          ) : viewMode === 'stepbystep' ? (
            <NavigationSimulator
              buildingData={buildingData}
              route={route}
              entrance={selectedEntrance}
              destination={selectedDestination}
              onDestinationChange={handleDestinationSelect}
              isMobile={true}
            />
          ) : (
            <Building3DViewer
              buildingData={buildingData}
              onNodeSelect={handleDestinationSelect}
              selectedDestination={selectedDestination}
              isMobile={true}
              isNavigating={false}
            />
          )}
        </Box>

        {/* Start Navigation Button (shown in overview mode) */}
        {viewMode === 'overview' && route && (
          <Box sx={{
            position: 'absolute',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            display: 'flex',
            gap: 2,
          }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<PlayArrowIcon />}
              onClick={handleStartNavigation}
              sx={{
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                boxShadow: 4,
              }}
            >
              Start Navigation
            </Button>
          </Box>
        )}

        {/* Back to Overview Button (shown in stepbystep mode) */}
        {viewMode === 'stepbystep' && (
          <Fab
            color="primary"
            size="medium"
            onClick={handleBackToOverview}
            sx={{
              position: 'absolute',
              bottom: 24,
              left: 24,
              zIndex: 10,
            }}
          >
            <ArrowBackIcon />
          </Fab>
        )}

        {/* Mobile Settings Drawer */}
        <SwipeableDrawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          onOpen={() => setMobileDrawerOpen(true)}
        >
          <MobileControlsContent />
        </SwipeableDrawer>
      </Box>
    );
  }

  return (
    <Box sx={{
      p: { xs: 1, sm: 2, md: 3 },
      height: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header - Hidden on mobile when in fullscreen */}
      <Box sx={{ mb: { xs: 1, md: 2 }, display: { xs: isFullscreen ? 'none' : 'block', md: 'block' } }}>
        {!isMobile && (
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 1 }}>
            <Link color="inherit" href="/dashboard" underline="hover">
              Dashboard
            </Link>
            <Link color="inherit" href="/indoor-mapping" underline="hover">
              Indoor Mapping
            </Link>
            <Typography color="text.primary">Navigation Preview</Typography>
          </Breadcrumbs>
        )}

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
            <NavigationIcon sx={{ fontSize: { xs: 28, md: 36 }, color: 'primary.main' }} />
            <Box>
              <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold">
                {isMobile ? 'Navigation Preview' : 'Indoor Navigation Preview'}
              </Typography>
              {!isMobile && (
                <Typography variant="body2" color="text.secondary">
                  Preview 3D building view and test indoor navigation
                </Typography>
              )}
            </Box>
          </Box>

          {/* Status indicator - Desktop */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {viewMode === 'overview' && route && (
                <>
                  <Chip
                    icon={<ThreeDIcon />}
                    label="Route Preview"
                    color="primary"
                  />
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PlayArrowIcon />}
                    onClick={handleStartNavigation}
                  >
                    Start Navigation
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleCancelNavigation}
                  >
                    Cancel
                  </Button>
                </>
              )}
              {viewMode === 'stepbystep' && (
                <>
                  <Chip
                    icon={<NavigationIcon />}
                    label="Navigating"
                    color="success"
                  />
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBackToOverview}
                  >
                    Back to Overview
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleCancelNavigation}
                  >
                    End
                  </Button>
                </>
              )}
              {viewMode === '3d' && !route && (
                <Typography variant="body2" color="text.secondary">
                  Select a destination to calculate route
                </Typography>
              )}
            </Box>
          )}

          {/* Mobile: Menu button */}
          {isMobile && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton onClick={() => setMobileDrawerOpen(true)}>
                <MenuIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>

      {/* Controls Bar - Desktop only */}
      {!isMobile && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Grid container spacing={2} alignItems="center">
              {/* Location Selection */}
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Building</InputLabel>
                  <Select
                    value={selectedLocationId}
                    label="Select Building"
                    onChange={(e) => {
                      setSelectedLocationId(e.target.value);
                      setViewMode('3d');
                      setRoute(null);
                      setSelectedDestination(null);
                    }}
                  >
                    <MenuItem value="">
                      <em>Choose a building...</em>
                    </MenuItem>
                    {locations
                      .sort((a, b) => (a.friendlyName || '').localeCompare(b.friendlyName || ''))
                      .map((loc) => (
                        <MenuItem key={loc.id} value={loc.id}>
                          {loc.friendlyName || loc.name || `Location ${loc.id}`}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Entrance Selection */}
              {entrances.length > 0 && (
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Start From Entrance</InputLabel>
                    <Select
                      value={selectedEntrance?.id || ''}
                      label="Start From Entrance"
                      onChange={(e) => {
                        const entrance = entrances.find(en => en.id === e.target.value);
                        setSelectedEntrance(entrance);
                      }}
                    >
                      {entrances.map((entrance) => (
                        <MenuItem key={entrance.id} value={entrance.id}>
                          <EntranceIcon sx={{ mr: 1, fontSize: 18 }} />
                          {entrance.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Destination info */}
              {selectedDestination && (
                <Grid item xs={12} md={3}>
                  <Paper variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NavigationIcon color="primary" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Destination
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedDestination.node?.name || `Node ${selectedDestination.node?.id}`}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              )}

              {/* Building Stats */}
              {stats && (
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Chip
                      icon={<LayersIcon />}
                      label={`${stats.floors} Floors`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      icon={<StairsIcon />}
                      label={stats.stairs}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      icon={<ElevatorIcon />}
                      label={stats.elevators}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      icon={<EntranceIcon />}
                      label={stats.entrances}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Mobile Controls Drawer */}
      <SwipeableDrawer
        anchor="right"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        onOpen={() => setMobileDrawerOpen(true)}
      >
        <MobileControlsContent />
      </SwipeableDrawer>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : !selectedLocationId ? (
          <Paper sx={{
            p: { xs: 2, md: 4 },
            textAlign: 'center',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <MapIcon sx={{ fontSize: { xs: 60, md: 80 }, color: 'text.secondary', mb: 2 }} />
            <Typography variant={isMobile ? 'h6' : 'h5'} gutterBottom>
              Select a Building
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {isMobile ? 'Tap the menu to choose a building' : 'Choose a building from the dropdown to preview its indoor navigation'}
            </Typography>
            {isMobile && (
              <Button
                variant="contained"
                startIcon={<MenuIcon />}
                onClick={() => setMobileDrawerOpen(true)}
              >
                Open Menu
              </Button>
            )}
          </Paper>
        ) : !buildingData || buildingData.floors.length === 0 ? (
          <Paper sx={{
            p: { xs: 2, md: 4 },
            textAlign: 'center',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <LayersIcon sx={{ fontSize: { xs: 60, md: 80 }, color: 'text.secondary', mb: 2 }} />
            <Typography variant={isMobile ? 'h6' : 'h5'} gutterBottom>
              No Floor Plans Found
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              This building doesn't have any floor plans configured yet.
            </Typography>
            <Button
              variant="contained"
              href="/indoor-mapping"
              startIcon={<MapIcon />}
            >
              Configure Indoor Mapping
            </Button>
          </Paper>
        ) : (
          <Box sx={{ height: '100%', position: 'relative' }}>
            {/* Fullscreen button for mobile */}
            {isMobile && !isFullscreen && (
              <Fab
                color="primary"
                size="medium"
                onClick={() => setIsFullscreen(true)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 10,
                }}
              >
                <FullscreenIcon />
              </Fab>
            )}

            {/* Mobile view mode indicator */}
            {isMobile && !isFullscreen && viewMode !== '3d' && (
              <Chip
                icon={viewMode === 'overview' ? <ThreeDIcon /> : <NavigationIcon />}
                label={viewMode === 'overview' ? 'Route Preview' : 'Navigating'}
                color={viewMode === 'stepbystep' ? 'success' : 'primary'}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  zIndex: 10,
                }}
              />
            )}

            {/* 3D View - Exploring building */}
            {viewMode === '3d' && (
              <Building3DViewer
                buildingData={buildingData}
                onNodeSelect={handleDestinationSelect}
                selectedDestination={selectedDestination}
                isMobile={isMobile}
                isNavigating={false}
              />
            )}

            {/* Overview Mode - 3D view with route displayed */}
            {viewMode === 'overview' && navigationRouteFor3D && (
              <>
                <Building3DViewer
                  buildingData={buildingData}
                  onNodeSelect={handleDestinationSelect}
                  selectedDestination={selectedDestination}
                  isMobile={isMobile}
                  isNavigating={true}
                  navigationRoute={navigationRouteFor3D}
                  currentStepIndex={currentStepIndex}
                />
                {/* Start Navigation button overlay for desktop */}
                {!isMobile && (
                  <Box sx={{
                    position: 'absolute',
                    bottom: 24,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                  }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={<PlayArrowIcon />}
                      onClick={handleStartNavigation}
                      sx={{
                        borderRadius: 3,
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        boxShadow: 4,
                      }}
                    >
                      Start Navigation
                    </Button>
                  </Box>
                )}
              </>
            )}

            {/* Step-by-step navigation mode */}
            {viewMode === 'stepbystep' && (
              <NavigationSimulator
                buildingData={buildingData}
                route={route}
                entrance={selectedEntrance}
                destination={selectedDestination}
                onDestinationChange={handleDestinationSelect}
                isMobile={isMobile}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default IndoorNavigationPreview;
